import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, Share2, Copy, ChevronLeft, Check, Clock } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { formatINR } from "../../lib/utils.fmt";
import { getTenantSlug } from "../../lib/tenant";
import { buildReferralShareUrl } from "../../lib/referralLink";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import LoginRequired from "./LoginRequired";
import EmptyState, { LoadingState } from "../../components/EmptyState";

export default function Referrals() {
  const { user, tenant } = useAuth();
  const [data, setData] = useState(null);

  const load = () => api.get("/api/customer/referrals/me").then((r) => setData(r.data)).catch((e) => {
    if (e.response?.status === 404) setData({ notEnabled: true });
  });
  useEffect(() => { if (user) load(); }, [user]);

  if (!user) return <LoginRequired title="Sign in to see your referral code"/>;
  if (!data) return <LoadingState fullHeight testId="referrals-loading" />;
  if (data.notEnabled) return (
    <EmptyState icon={Gift} title="Referrals aren't enabled" description="This store hasn't turned on referral rewards yet." fullHeight testId="referrals-disabled" />
  );

  const { code, referrals, stats } = data;
  const tenantName = tenant?.name || "the app";
  // Server-decided: would a reward actually be paid right now (referral
  // module active AND the admin's payout switch on)? Never inferred here —
  // a store can hand out codes (signup setting on) without paying rewards.
  const rewardsActive = data.rewards_active === true;
  const rewards = data.rewards || {};
  const referrerReward = rewardsActive ? Number(rewards.referrer_reward_inr) || 0 : 0;
  const refereeReward = rewardsActive ? Number(rewards.referee_reward_inr) || 0 : 0;
  const minOrder = Number(rewards.min_order_amount_inr) || 0;
  const firstOrder = minOrder > 0 ? `first order of ₹${minOrder}+` : "first order";
  const rewardLine = referrerReward > 0 && refereeReward > 0
    ? `you get ${formatINR(referrerReward)} and they get ${formatINR(refereeReward)}`
    : referrerReward > 0 ? `you get ${formatINR(referrerReward)}`
    : refereeReward > 0 ? `they get ${formatINR(refereeReward)}` : "";
  const shareText = refereeReward > 0
    ? `Hey! Try ${tenantName} for fresh dairy delivered to your door. Sign up with my code ${code} and get ${formatINR(refereeReward)} in your wallet once your ${firstOrder} is delivered.`
    : `Hey! Try ${tenantName} for fresh dairy delivered to your door. Use my code ${code} when you sign up.`;
  // Tenant-pinned: the friend has none of this device's tenant context.
  const shareUrl = typeof window !== "undefined"
    ? buildReferralShareUrl({ origin: window.location.origin, slug: tenant?.slug || getTenantSlug(), code })
    : "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Code copied!");
    } catch { toast.error("Copy failed"); }
  };

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share(shareUrl ? { title: tenantName, text: shareText, url: shareUrl } : { title: tenantName, text: shareText });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;   // customer closed the share sheet
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl ? `${shareText} ${shareUrl}` : shareText);
      toast.success("Invite copied — paste it to a friend");
    } catch { copy(); }
  };

  return (
    <div className="page-enter pb-16" data-testid="referrals-page">
      <header className="sticky top-0 z-30 bg-brand-cream/95 backdrop-blur pt-safe">
        <div className="px-5 pt-5 pb-4 flex items-center gap-2">
          <Link to="/app/me" className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center" data-testid="back-btn">
            <ChevronLeft className="w-5 h-5"/>
          </Link>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight flex items-center gap-2">
            <Gift className="w-6 h-6 text-brand-sky"/> {rewardLine ? "Refer & Earn" : "Refer a friend"}
          </h1>
        </div>
      </header>

      <main className="px-5 space-y-4">
        <div className="rounded-3xl bg-gradient-to-br from-brand-sky to-brand-leaf text-white p-6" data-testid="referral-hero">
          <div className="text-sm text-white/80">{rewardLine ? "Share your code · earn rewards" : "Share your code"}</div>
          <div className="font-heading text-xl mt-1" data-testid="referral-hero-offer">
            {referrerReward > 0 ? (
              <>Get <b>{formatINR(referrerReward)}</b> when your friend's {firstOrder} is delivered</>
            ) : refereeReward > 0 ? (
              <>Your friend gets <b>{formatINR(refereeReward)}</b> when their {firstOrder} is delivered</>
            ) : (
              <>Invite your friends to {tenantName}</>
            )}
          </div>
          <div className="mt-5 rounded-2xl bg-white/10 border-2 border-dashed border-white/30 p-4 text-center" data-testid="referral-code-card">
            <div className="text-xs uppercase tracking-wider text-white/70 font-bold">Your code</div>
            <div className="font-mono text-4xl font-extrabold tracking-widest mt-1" data-testid="referral-code">{code}</div>
          </div>
          <div className="mt-3 flex gap-2">
            <Button onClick={copy} className="flex-1 h-12 rounded-2xl bg-white text-brand-ink font-extrabold" data-testid="copy-code">
              <Copy className="w-4 h-4 mr-1.5"/> Copy
            </Button>
            <Button onClick={share} className="flex-1 h-12 rounded-2xl bg-brand-ink text-white font-extrabold" data-testid="share-code">
              <Share2 className="w-4 h-4 mr-1.5"/> Share
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2" data-testid="referral-stats">
          <Stat label="Sent" value={stats.total ?? 0}/>
          <Stat label="Pending" value={stats.pending ?? 0}/>
          <Stat label="Earned" value={formatINR(stats.earned_inr ?? 0)}/>
        </div>

        <div className="bg-white rounded-3xl border border-neutral-200 p-5" data-testid="how-it-works">
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-2">How it works</div>
          <ol className="text-sm text-neutral-700 space-y-1.5 list-decimal pl-5">
            <li>Share your code with a friend.</li>
            <li>They sign up on {tenantName} using your code.</li>
            {rewardLine && (
              <li data-testid="how-it-works-reward">When their {firstOrder} is delivered and paid for, {rewardLine}.</li>
            )}
          </ol>
        </div>

        <div className="bg-white rounded-3xl border border-neutral-200 p-5">
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-3">Your referrals</div>
          {referrals.length === 0 ? (
            <EmptyState icon={Gift} size="sm" title="No referrals yet"
                        description="No one has used your code yet — share it now!"
                        className="py-6" testId="referrals-list-empty" />
          ) : (
            <ul className="space-y-2">
              {referrals.map((r) => (
                <li key={r.id} className="flex items-center justify-between text-sm" data-testid={`my-referral-${r.id}`}>
                  <div className="flex items-center gap-2">
                    {r.status === "fulfilled" ? (
                      <Check className="w-4 h-4 text-brand-leaf"/>
                    ) : (
                      <Clock className="w-4 h-4 text-brand-sand"/>
                    )}
                    <span className="font-bold">{r.referee_name || "Friend"}</span>
                  </div>
                  <div className="text-right">
                    <div className={`text-xs font-extrabold uppercase tracking-wider ${r.status === "fulfilled" ? "text-brand-leaf" : "text-brand-sand"}`}>
                      {r.status === "fulfilling" ? "processing" : r.status}
                    </div>
                    {r.status === "fulfilled" && (
                      <div className="font-mono font-bold text-brand-leaf">+ {formatINR(r.referrer_credit || 0)}</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white rounded-2xl border border-neutral-200 p-3 text-center">
      <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">{label}</div>
      <div className="font-heading text-xl font-extrabold mt-0.5">{value}</div>
    </div>
  );
}
